import React, { useState, type SetStateAction } from "react";
import {
    Alert,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from "@mui/material";
import { useTranslation } from "shared/hooks/useTranslation";
import { DialogCard } from "shared/ui/ComposedCards";
import MoneyInput from "shared/ui/MoneyInput";
import FormDialog from "shared/ui/FormDialog";
import FormActions from "shared/ui/FormActions";
import { FORM_FIELD_GAP } from "shared/ui/formConstants";
import ConfirmDialog from "shared/ui/ConfirmDialog";

const CURRENCIES = ["ARS", "USD", "USDT", "USDC"] as const;

export interface AddPoolCardProps {
    onSubmit: (data: { name: string; currency: string; initialBalance?: number }) => Promise<{ success: boolean; error?: string }>;
    isOpen?: boolean;
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    open?: boolean;
    onClose?: () => void;
    variant?: "inline" | "dialog";
    /** When true, submit is disabled (e.g. offline read-only). */
    mutationsDisabled?: boolean;
}

const AddPoolCard: React.FC<AddPoolCardProps> = ({
    onSubmit,
    isOpen: controlledIsOpen,
    setIsOpen: setControlledIsOpen,
    open,
    onClose,
    variant = "inline",
    mutationsDisabled = false,
}) => {
    const { t } = useTranslation();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isOpen = open ?? controlledIsOpen ?? internalIsOpen;
    const setIsOpen = onClose
        ? (next: SetStateAction<boolean>) => {
            const resolved = typeof next === "function" ? next(isOpen) : next;
            if (!resolved) onClose();
        }
        : (setControlledIsOpen ?? setInternalIsOpen);
    const [name, setName] = useState("");
    const [currency, setCurrency] = useState("ARS");
    const [initialBalance, setInitialBalance] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    const handleReset = () => {
        setName("");
        setCurrency("ARS");
        setInitialBalance("");
        setError(null);
    };

    const requestClose = () => {
        if (submitting) return;
        if (name.trim() || initialBalance || currency !== "ARS") {
            setConfirmDiscard(true);
            return;
        }
        handleReset();
        setIsOpen(false);
    };

    const handleSubmit = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError(t("validation.required"));
            return;
        }
        const balance = initialBalance ? parseFloat(initialBalance) : 0;
        if (initialBalance && (isNaN(balance) || balance < 0)) {
            setError(t("wallet.invalidInitialBalance"));
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const result = await onSubmit({
                name: trimmedName,
                currency,
                initialBalance: balance,
            });
            if (result.success) {
                handleReset();
                setIsOpen(false);
            } else {
                setError(result.error ?? t("common.error"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const inner = (
        <Stack spacing={FORM_FIELD_GAP}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
                label={t("wallet.poolName")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                size="small"
            />
            <FormControl fullWidth size="small">
                <InputLabel>{t("forms.currency")}</InputLabel>
                <Select
                    value={currency}
                    label={t("forms.currency")}
                    onChange={(e) => setCurrency(e.target.value)}
                >
                    {CURRENCIES.map((c) => (
                        <MenuItem key={c} value={c}>
                            {c}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <MoneyInput
                label={t("wallet.initialBalance")}
                value={initialBalance}
                onValueChange={setInitialBalance}
                fullWidth
                size="small"
                helperText={t("wallet.initialBalanceHint")}
            />
            <FormActions
                submitLabel={t("wallet.saveAccountRecord")}
                submitButtonType="button"
                onSubmit={handleSubmit}
                onCancel={requestClose}
                submitDisabled={!name.trim() || mutationsDisabled}
                submitLoading={submitting}
            />
        </Stack>
    );

    return (
        variant === "dialog" ? (
            <>
                <FormDialog
                    open={isOpen}
                    onClose={requestClose}
                    title={t("wallet.addPool")}
                    disabled={submitting}
                >
                    {inner}
                </FormDialog>
                <ConfirmDialog
                    open={confirmDiscard}
                    title={t("forms.discardChangesTitle")}
                    description={t("forms.discardChangesDescription")}
                    cancelLabel={t("forms.keepEditing")}
                    confirmLabel={t("forms.discardDraft")}
                    confirmColor="error"
                    onCancel={() => setConfirmDiscard(false)}
                    onConfirm={() => {
                        setConfirmDiscard(false);
                        handleReset();
                        setIsOpen(false);
                    }}
                />
            </>
        ) : (
            <DialogCard title={t("wallet.addPool")} triggerLabel={t("wallet.addPool")} disabled={submitting} open={isOpen} onOpenChange={setIsOpen} onRequestClose={requestClose}>{<CardContent>{inner}</CardContent>}</DialogCard>
        )
    );
};

export default AddPoolCard;
